import {
	$$,
	ShortDate,
	ShortTime,
	createNotification,
} from "../utils/dom";
import shared from "../data/js/shared";
import { SPAM, INAPPROPRIATE, OTHER } from "../data/constants";
import { useUI, useNotifications, useProduct, useChat, useRouterContext, useUser } from "../contexts";
import SubMenuWrapper from "./sub-menu-wrapper";
import { trackError } from "../utils/error-tracking";
import ReportListingPopup from "./report-listing-popup";
import { Share } from '@capacitor/share';
import { useState, RefObject } from "react";
import FacebookAPI from "../utils/facebook";
import { IS_MOBILE } from "../data/js/server-constants";
import type { Chat } from "../contexts/ChatMessageProvider";
import type { Product } from "../contexts/ProductProvider";

const {
	reportSpamLabel,
	inappropriateLabel,
	otherLabel,
	facebookLabel,
	twitterLabel,
	copyLinkLabel,
	copiedLabel,
	emailLabel,
	deleteMessageLabel,
	deleteChatLabel,
	copyMessageLabel,
	copyChatLabel,
	viewMessagesLabel,
	clipboardAccessDeniedNotice,
} = shared.submenus;

const getHref = () => {
	const fallback = $$(".social-canonical")[1].content;
	return IS_MOBILE ? fallback : window.location.href;
};

const facebookShare = async () => {
	if (IS_MOBILE) {
		await Share.share({
			url: getHref(),
		});
		return;
	}
	const fb = new FacebookAPI();
	const href = getHref();
	await fb.share(href);
};

const twitterShare = async () => {
	if (IS_MOBILE) {
		await Share.share({
			url: getHref(),
		});
		return;
	}
	const href = getHref();
	window.open("https://twitter.com/share?url=" + encodeURIComponent(href));
};

const copyLink = async () => {
	if (IS_MOBILE) {
		await Share.share({
			url: getHref(),
		});
		return;
	}
	const href = getHref();
	void navigator.clipboard.writeText(href);
};

const copyMessage = async (msg: string) => {
	try {
		await navigator.clipboard.writeText(msg);
	} catch (e: unknown) {
		const error = e as Error;
		if (error.name.includes("NotAllowedError")) {
			createNotification({
				type: "error",
				msg: clipboardAccessDeniedNotice,
			});
		} else {
			// A denied clipboard is the reader's own setting; anything else is ours.
			trackError(error, 'warning', { component: 'submenu', action: 'copy-message' });
			createNotification({
				type: "error",
				msg: error.message,
			});
		}
	}
};

const openMail = () => {
	const href = getHref();
	const title = document
		.querySelector(".social-meta-title")
		.getAttribute("content");
	const email =
		"mailto:?body=Check out this link " +
		href +
		" on Sellwild Marketplace&subject=" +
		title;
	window.open(email);
};

interface ReportSubMenuProps {
	id: string | number;
	triggerRef?: RefObject<HTMLElement | null>;
}

const ReportSubMenu = ({ id, triggerRef }: ReportSubMenuProps) => {
	const { reportProduct, productDetail } = useProduct();
	const { setReportMenuOpen, reportMenuOpen, setPopupContents, setPopupOpen } = useUI();
	const { createNotification } = useNotifications();

	const product: Product = productDetail || { id: String(id), title: '', price: 0, user: { id: '' } };

	const handleReport = async (flagType: string, reason: string) => {
		setReportMenuOpen(false);

		try {
			const success = await reportProduct(product, flagType, reason);

			if (success) {
				createNotification({
					type: 'success',
					msg: `${shared.product.reportedNotification1} - ${shared.product.reportedNotification2}`,
					dismissed: false,
				});
			} else {
				createNotification({
					type: 'error',
					msg: shared.product.reportError,
					dismissed: false,
				});
			}
		} catch (error) {
			trackError(error instanceof Error ? error : new Error(String(error)), 'error', {
				component: 'submenu',
				action: 'report-listing',
				extra: { listingId: product.id, flagType },
			});
			createNotification({
				type: 'error',
				msg: shared.product.reportError,
				dismissed: false,
			});
		}
	};

	/**
	 * "Other" asks first. The other reasons describe themselves; this one filed a
	 * report whose reason was the literal word "Other", which told the admins
	 * nothing they did not already have from the report type. The message the
	 * person types becomes the report's `reason`, which the admin email renders.
	 *
	 * Closes the menu before opening the popup: both are overlays, and leaving
	 * the menu up puts it over the box being typed into on mobile.
	 */
	const openOtherReport = () => {
		setReportMenuOpen(false);

		const closePopup = () => {
			setPopupOpen(false);
			setPopupContents(null);
		};

		setPopupContents(
			<ReportListingPopup
				listingTitle={product.title}
				onCancel={closePopup}
				onSubmit={(message: string) => {
					closePopup();
					void handleReport(OTHER, message);
				}}
			/>
		);
		setPopupOpen(true);
	};

	return (
			<SubMenuWrapper
				className="sub-menu-cont report-menu-cont"
				onChange={() => {
					setReportMenuOpen(false);
				}}
				handle="report-submenu-handle"
				expanded={reportMenuOpen}
				triggerRef={triggerRef}
				dynamicPosition={true}
			>
				<div className="sub-menu report-menu" data-screenshot="report-menu">
					<div
						className="sub-menu-item"
						data-testid="report-spam"
						onClick={() => handleReport(SPAM, reportSpamLabel)}
					>
						{reportSpamLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						data-testid="report-inappropriate"
						onClick={() => handleReport(INAPPROPRIATE, inappropriateLabel)}
					>
						{inappropriateLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						data-testid="report-other"
						onClick={openOtherReport}
					>
						{otherLabel}
					</div>
					<img
						src="/images/submenu-pointer.png"
						className="pointer"
						alt=""
					/>
				</div>
			</SubMenuWrapper>
	);
};

interface ShareSubMenuProps {
	triggerRef?: RefObject<HTMLElement | null>;
}

const ShareSubMenu = ({ triggerRef }: ShareSubMenuProps) => {
	const { setShareMenuOpen, shareMenuOpen } = useUI();
	const [copied, setCopied] = useState(false);
	return (
			<SubMenuWrapper
				className="sub-menu-cont share-menu-cont"
				onChange={() => {
					setShareMenuOpen(false);
				}}
				handle="share-submenu-handle"
				expanded={shareMenuOpen}
				triggerRef={triggerRef}
				dynamicPosition={true}
			>
				<div className="sub-menu share-menu">
					<div
						className="sub-menu-item"
						onClick={() => {
							void facebookShare();
							setShareMenuOpen(false);
						}}
					>
						{facebookLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						onClick={() => {
							void twitterShare();
							setShareMenuOpen(false);
						}}
					>
						{twitterLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						onClick={() => {
							void copyLink();
							setCopied(true);
							setTimeout(() => {
								setCopied(false);
								setShareMenuOpen(false);
							}, 2000);
						}}
					>
						{copied ? copiedLabel : copyLinkLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						onClick={() => {
							openMail();
							setShareMenuOpen(false);
						}}
					>
						{emailLabel}
					</div>
					<img
						src="/images/submenu-pointer.png"
						className="pointer"
						alt=""
					/>
				</div>
			</SubMenuWrapper>
	);
};

interface ChatSubMenuProps {
	activeChat: Chat;
	/** Updated after a delete; absent where there is no list on screen (the chat modal). */
	setChatList?: (chats: Chat[]) => void;
	triggerRef?: RefObject<HTMLElement | null>;
	dynamicPosition?: boolean;
}

const ChatSubMenu = ({ activeChat, setChatList, triggerRef, dynamicPosition }: ChatSubMenuProps) => {
	const { setChatMenuOpen, chatMenuOpen } = useUI();
	const { deleteChat, fetchThreadMessages } = useChat();
	const { profile } = useUser();
	const [copied, setCopied] = useState(false);
	const { push } = useRouterContext();
	const chatId = activeChat.id;

	const chatURL = "/account/chats/" + chatId;

	const clickCopyThread = async (_e: React.MouseEvent) => {
		const msgText = await copyWholeThread(activeChat.id);
		await copyMessage(msgText);
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
			setChatMenuOpen(false);
		}, 2000);
	};

	// Each line names who sent it. It used to look for a `me` on the chat,
	// which the chat list never sends, so every line -- yours too -- carried
	// the other person's name. The lines also ran together on one line.
	const copyWholeThread = async (threadChatId: string) => {
		const thread = await fetchThreadMessages(threadChatId);
		if (!thread) return "";
		const myId = profile?.id ? String(profile.id) : null;
		const myName =
			`${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim() || "Me";
		const theirName = thread.companion?.name || activeChat.companion?.name || "";
		return thread.messages
			.map((m) => {
				const sender = myId !== null && String(m.senderId) === myId ? myName : theirName;
				return `${ShortDate(m.createdDate)} ${ShortTime(
					m.createdDate,
				)} - ${sender} ${m.text ?? m.message ?? ""}`;
			})
			.join("\n");
	};

	return (
		<SubMenuWrapper
			className="sub-menu-cont chat-menu-cont"
			onChange={() => {
				setChatMenuOpen(false);
			}}
			handle="chat-submenu-handle"
			expanded={chatMenuOpen}
			triggerRef={triggerRef}
			dynamicPosition={dynamicPosition}
		>
			<div className="sub-menu chat-menu">
				<div
					className="sub-menu-item"
					onClick={() => {
						setChatMenuOpen(false);
						push(chatURL);
					}}
				>
					{viewMessagesLabel}
				</div>
				<div className="line" />
				<div
					className="sub-menu-item"
					onClick={() => {
						void deleteChat(activeChat.id, setChatList);
						setChatMenuOpen(false);
					}}
				>
					{deleteChatLabel}
				</div>
				<div className="line" />
				<div className="sub-menu-item" onClick={clickCopyThread}>
					{copied ? copiedLabel : copyChatLabel}
				</div>
				<img
					src="/images/submenu-pointer.png"
					className="pointer"
					alt="Menu pointer arrow"
				/>
			</div>
		</SubMenuWrapper>
	);
};

interface MessageSubMenuProps {
	activeMessage: { id: string; chatId: string; text: string };
	/** Updated after a delete; absent where there is no list on screen (the chat modal). */
	setChatList?: (chats: Chat[]) => void;
	setActiveMessage: (msg: Record<string, unknown>) => void;
	triggerRef?: RefObject<HTMLElement | null>;
}

const MessageSubMenu = ({ activeMessage, setChatList, setActiveMessage, triggerRef }: MessageSubMenuProps) => {
	const { setMessageMenuOpen, messageMenuOpen } = useUI();
	const { deleteMessage, deleteChat } = useChat();
	const [copied, setCopied] = useState(false);
	return (
			<SubMenuWrapper
				className="sub-menu-cont message-menu-cont"
				onChange={() => {
					setMessageMenuOpen(false);
				}}
				handle="message-submenu-handle"
				expanded={messageMenuOpen}
				triggerRef={triggerRef}
				dynamicPosition={true}
			>
				<div className="sub-menu message-menu">
					<div
						className="sub-menu-item"
						onClick={() => {
							void deleteMessage(activeMessage.id);
							setMessageMenuOpen(false);
						}}
					>
						{deleteMessageLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						onClick={() => {
							void deleteChat(activeMessage.chatId, setChatList);
							setMessageMenuOpen(false);
						}}
					>
						{deleteChatLabel}
					</div>
					<div className="line" />
					<div
						className="sub-menu-item"
						onClick={() => {
							void copyMessage(activeMessage.text);
							setCopied(true);
							setTimeout(() => {
								setCopied(false);
								setMessageMenuOpen(false);
								setActiveMessage({});
							}, 2000);
						}}
					>
						{copied ? copiedLabel : copyMessageLabel}
					</div>
					<img
						src="/images/submenu-pointer.png"
						className="pointer"
						alt=""
					/>
				</div>
			</SubMenuWrapper>
	);
};

export { ReportSubMenu, ShareSubMenu, ChatSubMenu, MessageSubMenu };
